// frontend/src/pages/NewUIHome.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../config/axios';
import GoogleAdBanner from '../components/newui/GoogleAdBanner';

// ============================================================================
// COMPONENTE GAMINGBUTTON ACTUALIZADO - AHORA ACEPTA PROP "to" PARA NAVEGACIÓN
// ============================================================================
// Uso como Link:    <GamingButton to="/ruta">Texto</GamingButton>
// Uso como Button:  <GamingButton onClick={handleClick}>Texto</GamingButton>
// ============================================================================
function GamingButton({ variant = "secondary", className = "", leftIcon, to, children, ...props }) {
    const base =
        "inline-flex items-center justify-center gap-2.5 " +
        "h-12 md:h-14 px-6 md:px-8 " +
        "rounded-[14px] md:rounded-[16px] " +
        "font-bold tracking-wider leading-none " +
        "transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98]";

    const variants = {
        primary: "bg-accent text-secondary hover:bg-accent-light border-2 border-accent-dark",
        secondary: "bg-primary text-white hover:bg-primary-light border-2 border-primary-dark",
        outline: "bg-transparent border-2 border-accent text-accent hover:bg-accent/10",
        ctaBlue: "bg-[#2284DF] text-white hover:bg-[#2A91F4] border-2 border-[#1E75C6]",
        ctaYellow: "bg-[#FCE503] text-[#122436] hover:bg-[#FCE503]/90 shadow-[0_10px_30px_rgba(252,229,3,0.35)]",
        ctaOutlineYellow: "bg-[#0E2238]/80 border-2 border-[#FCE503] text-[#FCE503] hover:bg-[#FCE50314]",
    };

    // Si tiene "to", se renderiza como Link, sino como button
    const Component = to ? Link : 'button';

    return (
        <Component
            {...(to ? { to } : {})}
            className={`${base} ${variants[variant]} ${className}`}
            {...props}
        >
            {leftIcon ? <span className="inline-flex items-center">{leftIcon}</span> : null}
            <span className="whitespace-nowrap">{children}</span>
        </Component>
    );
}

function elapsedHome(startTime) {
    if (!startTime) return null;
    const secs = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

const ActiveMatchCard = ({ match }) => {
    const isTournament = match.isTournament;
    const [p1, p2] = match.players;

    return (
        <div className="relative rounded-[18px] overflow-hidden bg-[#122436] ring-2 ring-white shadow-[0_12px_40px_rgba(0,0,0,0.35)] transition-transform duration-200 hover:-translate-y-0.5 flex flex-col">
            {/* Header */}
            <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    {isTournament ? (
                        <>
                            <p className="text-[#FCE503] text-[11px] font-bold uppercase tracking-wider mb-0.5">
                                🏆 Torneo · {match.tournament?.game?.name ?? match.game?.name ?? 'Truco'}
                            </p>
                            <p className="text-white font-bold text-base leading-tight truncate">
                                {match.tournament.name}
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-white/50 text-[11px] font-bold uppercase tracking-wider mb-0.5">
                                {match.game?.name ?? 'Partida rápida'}
                            </p>
                            <p className="text-white font-bold text-base leading-tight">
                                Partida rápida
                            </p>
                        </>
                    )}
                </div>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold uppercase ${
                    match.status === 'IN_PROGRESS'
                        ? 'bg-[#FCE503] text-[#0E2238]'
                        : 'border-2 border-[#FCE503] text-[#FCE503]'
                }`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                    {match.status === 'IN_PROGRESS' ? 'En juego' : 'Preparando'}
                </span>
            </div>

            <div className="mx-5 border-t border-white/10" />

            {/* Jugadores */}
            <div className="px-5 py-4 flex-1">
                <div className="flex items-center gap-3">
                    <div className="flex-1 bg-[#0E2238] rounded-lg px-3 py-2.5 min-w-0">
                        {p1.members.length > 0 ? (
                            <>{p1.members.map((m, i) => <p key={i} className="text-white text-sm font-semibold truncate">{m}</p>)}</>
                        ) : (
                            <p className="text-white text-sm font-semibold truncate">{p1.label}</p>
                        )}
                    </div>
                    <span className="text-white/30 font-black text-sm flex-shrink-0">VS</span>
                    <div className="flex-1 bg-[#0E2238] rounded-lg px-3 py-2.5 min-w-0 text-right">
                        {p2.members.length > 0 ? (
                            <>{p2.members.map((m, i) => <p key={i} className="text-white text-sm font-semibold truncate">{m}</p>)}</>
                        ) : (
                            <p className="text-white text-sm font-semibold truncate">{p2.label}</p>
                        )}
                    </div>
                </div>
                {isTournament && match.tournament?.prizePool && (
                    <p className="mt-3 text-right text-sm font-bold text-[#FCE503]">
                        Premio: {match.tournament.prizePool.toLocaleString('es-AR')} tickets
                    </p>
                )}
                {match.startTime && (
                    <div className="mt-2 flex items-center gap-1.5 text-white/40 text-xs">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {elapsedHome(match.startTime)} en curso
                    </div>
                )}
            </div>
        </div>
    );
};

const PlayIconOutline = ({ className = "w-6 h-6 md:w-7 md:h-7" }) => (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path
            d="M8 6.75 L8 17.25 L17 12 Z"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinejoin="round"
            strokeLinecap="round"
            fill="none"
        />
    </svg>
);

const GameCard = ({ title, img, available, to, onClick }) => {
    const bgStyle = img
        ? { backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center" }
        : { backgroundImage: "none", backgroundColor: available ? "#fce503" : "#122436" };

    const interactive = available && (to || onClick);
    const Component = to ? Link : 'button';

    return (
        <Component
            {...(to ? { to } : { type: "button", onClick })}
            aria-disabled={!interactive}
            className={[
                "group relative block rounded-[22px] overflow-hidden ring-1 ring-black/10",
                "transition-all duration-200 ease-out transform-gpu",
                interactive
                    ? "cursor-pointer hover:scale-[1.03] active:scale-[0.99] hover:shadow-[0_18px_50px_rgba(34,132,223,0.45)] hover:ring-2 hover:ring-[#2A91F4]/70"
                    : "cursor-not-allowed opacity-95",
                "focus:outline-none focus-visible:ring-4 focus-visible:ring-[#2A91F4]/70 focus-visible:ring-offset-0"
            ].join(" ")}
        >
            <div className="w-full aspect-[1/1] sm:aspect-[119/120]" style={bgStyle} />

            <div className="absolute inset-x-0 top-0 pt-4 sm:pt-5 text-center pointer-events-none">
        <span
            className="toon toon-3d inline-block text-4xl sm:text-5xl font-extrabold uppercase text-white"
            style={{ WebkitTextStroke: "1px #000",
                textShadow: "0 2px 0 rgba(0,0,0,1), 0 4px 2px rgba(0,0,0,1)" }}
        >
          {title}
        </span>
            </div>

            <div className="absolute left-0 right-0 bottom-10">
                {available ? (
                    <div className="flex items-center gap-2 justify-center h-10 md:h-11 bg-[#2284DF] text-white font-extrabold tracking-wide uppercase shadow-lg">
                        <svg viewBox="0 0 24 24" className="w-4.5 h-4.5 md:w-5 md:h-5" fill="none" aria-hidden="true">
                            <path d="M8.2 7 L8.2 17 L16.6 12 Z" stroke="currentColor" strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round"/>
                        </svg>
                        <span className="text-xs sm:text-sm">Jugar ahora</span>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-10 md:h-11 bg-[#E02020] text-white font-extrabold tracking-wide uppercase shadow-lg">
                        <span className="text-xs sm:text-sm">Próximamente</span>
                    </div>
                )}
            </div>
        </Component>
    );
};

const PlayIconOutlineRounded = ({ className = "w-4.5 h-4.5 md:w-5 md:h-5" }) => (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path
            d="M8.2 7 L8.2 17 L16.6 12 Z"
            stroke="currentColor"
            strokeWidth="2.8"
            strokeLinejoin="round"
            strokeLinecap="round"
            fill="none"
        />
    </svg>
);

const MaskIcon = ({ src, className = "w-5 h-5" }) => (
    <span
        aria-hidden="true"
        className={`inline-block ${className}`}
        style={{
            backgroundColor: "currentColor",
            WebkitMaskImage: `url(${src})`,
            maskImage: `url(${src})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
        }}
    />
);

const WinnerCardMobile = ({ username, gameName, sala, avatarColor }) => {
    const colors = {
        red: 'bg-red-700',
        blue: 'bg-blue-800',
        green: 'bg-green-700'
    };

    return (
        <div className="flex items-center space-x-3 py-3">
            <div className={`w-10 h-10 rounded-full ${colors[avatarColor] || 'bg-primary'} flex items-center justify-center`}>
                <span className="text-white">{username[0]}</span>
            </div>
            <div className="flex flex-col">
                <p className="text-primary font-medium">
                    <span className="font-bold">{gameName}</span> | {username}
                </p>
                <p className="text-sm text-gray-500">Sala {sala}</p>
            </div>
        </div>
    );
};

const WinnerCard = ({ username, gameName, serie }) => (
    <div className="flex items-center space-x-3 py-2">
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <span className="text-white">{username[0]}</span>
        </div>
        <div>
            <p className="text-primary font-medium">{gameName}</p>
            <p className="text-sm text-gray-600">Serie {serie}</p>
        </div>
    </div>
);

const ComingSoonCard = ({ title, imageSrc }) => (
    <div className="bg-secondary rounded-lg p-6 flex justify-between items-start h-48">
        <div className="flex flex-col h-full justify-between">
            <img src={`${imageSrc}.png`} alt={title} className="h-12 object-contain" />
            <button
                className="px-4 py-2 bg-gray-600 text-white rounded-full cursor-not-allowed"
                disabled
            >
                Próximamente
            </button>
        </div>
        <div className="w-1/2">
            <img src={`${imageSrc}-featured.png`} alt={`${title} featured`} className="h-full w-full object-contain" />
        </div>
    </div>
);

const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
        <div className="bg-white rounded-lg shadow">
            <button
                className="w-full px-6 py-4 text-left flex justify-between items-center"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="font-medium text-primary">{question}</span>
                <ChevronDown
                    className={`w-5 h-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                />
            </button>
            {isOpen && (
                <div className="px-6 py-4 border-t">
                    <p className="text-gray-600">{answer}</p>
                </div>
            )}
        </div>
    );
};

const NewUIHome = () => {
    const navigate = useNavigate();

    useEffect(() => {
        document.body.classList.add('acumin-scope');
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.classList.remove('acumin-scope');
            document.body.style.overflowX = '';
        };
    }, []);

    // Partidas activas: hasta 3 aleatorias
    const [activeMatches, setActiveMatches] = useState([]);
    useEffect(() => {
        axios.get('/matches/active')
            .then(res => {
                if (res.data.success) {
                    const all = res.data.matches;
                    // Barajar y tomar hasta 3
                    const shuffled = [...all].sort(() => Math.random() - 0.5);
                    setActiveMatches(shuffled.slice(0, 3));
                }
            })
            .catch(() => {}); // silencioso en home
    }, []);

    const [open, setOpen] = useState(null);
    const PANEL_ALPHA = 0.28, BORDER_ALPHA = 0.55;
    const SECTION_BG = "#2284DF";
    const panelBg = `rgba(0,0,0,${PANEL_ALPHA})`;
    const borderCol = `rgba(255,255,255,${BORDER_ALPHA})`;
    const faqs = [
        {
            q: "¿Cómo empiezo a jugar en Mano Ganadora?",
            a: "Creá tu cuenta, verificá tu email y completá el perfil. Luego elegí un juego, ingresá a una mesa o partida rápida y seguí las instrucciones en pantalla.",
        },
        {
            q: "¿Es seguro jugar y hacer depósitos?",
            a: "Usamos cifrado de datos y proveedores de pago auditados. Nunca almacenamos información sensible de tus tarjetas en nuestros servidores.",
        },
        {
            q: "¿Cómo funciona el sistema de niveles?",
            a: "Sumás experiencia (XP) al jugar partidas. Al subir de nivel, desbloqueás beneficios temporales, tickets y acceso a torneos exclusivos.",
        },
        {
            q: "¿Qué métodos de pago aceptan?",
            a: "Mercado Pago (saldo, tarjeta o transferencia). Según tu país también pueden aparecer métodos locales adicionales.",
        },
        {
            q: "¿Cuánto tiempo tardan los retiros?",
            a: "Los retiros se acreditan generalmente en minutos, aunque pueden demorar hasta 24 h según el método y validaciones antifraude.",
        },
        {
            q: "¿Hay límites de depósito o retiro?",
            a: "Sí, varían según tu verificación de identidad y el método. Podés ver y aumentar tus límites desde tu perfil.",
        },
        {
            q: "¿Ofrecen torneos especiales?",
            a: "Sí, organizamos torneos periódicos con diferentes buy-ins y premios. Revisá el calendario en la sección Torneos.",
        },
        {
            q: "¿Puedo jugar desde mi celular?",
            a: "Claro. La plataforma es responsive y compatible con navegadores móviles modernos.",
        },
    ];

    const { isAuthenticated } = useAuth();

    return (
        <div className="min-h-screen overflow-x-hidden">
            {/* Hero Section */}
            <section
                className="relative -mt-16 pt-16"
                style={{
                    backgroundImage: 'url(/images/backgrounds/hero-radial-bg.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
                <div className="max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="min-h-[100svh] flex flex-col items-center justify-center text-center gap-4 lg:gap-6 pt-2">

                        {/* LOGO */}
                        <div
                            aria-hidden="true"
                            className="
                                pointer-events-none 
                                w-[min(90vw,1500px)]
                                h-[clamp(26rem,52vw,35rem)]
                                mx-auto drop-shadow-2xl
                                scale-125 md:scale-[1.6] 2xl:scale-[1.8] origin-center
                            "
                            style={{
                                backgroundImage: "url(/images/logos/logo-ornate.png)",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center",
                                backgroundSize: "contain",
                            }}
                        />

                        {/* Wrapper CTA + Stats */}
                        <div className="
                          flex flex-col items-center text-center gap-6 lg:gap-7
                          -mt-6 sm:-mt-8 lg:-mt-10
                          transform-gpu
                          -translate-x-2 sm:-translate-x-3 md:-translate-x-5 lg:-translate-x-6 xl:-translate-x-8
                        ">
                            {/* ================================================== */}
                            {/* BOTONES CON NAVEGACIÓN - CAMBIÁ EL PROP "to" AQUÍ */}
                            {/* ================================================== */}
                            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                                <GamingButton
                                    to="/games"
                                    variant="ctaBlue"
                                    className="min-w-[220px] md:min-w-[260px]"
                                    leftIcon={
                                        <span className="inline-flex items-center justify-center w-6 h-6 md:w-7 md:h-7 rounded-full bg-white/12 ring-1 ring-white/20 shrink-0">
                                            <PlayIconOutlineRounded className="w-5 h-5 md:w-5.5 md:h-5.5" />
                                        </span>}>
                                    JUGAR AHORA
                                </GamingButton>

                                <GamingButton
                                    to="/active-matches"
                                    variant="ctaOutlineYellow"
                                    className="min-w-[220px] md:min-w-[260px]"
                                    leftIcon={<MaskIcon src="/images/icons/users.png" className="w-5 h-5 md:w-6 md:h-6" />}
                                >
                                    VER PARTIDAS ACTIVAS
                                </GamingButton>
                            </div>
                            {/* ================================================== */}
                            {/* FIN DE BOTONES - Para cambiar las rutas, modificá  */}
                            {/* el prop "to" de cada GamingButton                   */}
                            {/* ================================================== */}

                            {/* Stats */}
                            <div className="
                              w-full max-w-5xl mx-auto
                              text-white/85
                              flex flex-col md:flex-row items-stretch justify-center
                              gap-4 md:gap-0
                              md:divide-x md:divide-white/15
                            ">
                                <div className="flex-1 px-4 md:px-6 text-center">
                                    <div className="text-[11px] md:text-xs tracking-[0.16em] opacity-80 leading-tight min-h-[2em] flex items-end justify-center">
                                        JUGADORES ACTIVOS
                                    </div>
                                    <div className="text-3xl md:text-4xl font-extrabold text-accent tabular-nums drop-shadow-[0_0_6px_rgba(252,229,3,0.25)]">
                                        2,500
                                    </div>
                                </div>

                                <div className="flex-1 px-4 md:px-6 text-center">
                                    <div className="text-[11px] md:text-xs tracking-[0.16em] opacity-80 leading-tight min-h-[2.7em] flex items-center justify-center">
                                        TORNEOS
                                    </div>
                                    <div className="text-3xl md:text-4xl font-extrabold text-accent drop-shadow-[0_0_6px_rgba(252,229,3,0.25)]">
                                        DIARIOS
                                    </div>
                                </div>

                                <div className="flex-1 px-4 md:px-6 text-center">
                                    <div className="text-[11px] md:text-xs tracking-[0.16em] opacity-80 leading-tight min-h-[2.7em] flex items-center justify-center">
                                        PARTIDAS
                                    </div>
                                    <div className="text-3xl md:text-4xl font-extrabold text-accent drop-shadow-[0_0_6px_rgba(252,229,3,0.25)]">
                                        24/7
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative z-10 px-4 pt-6 pb-8 sm:px-6 lg:px-8" style={{ backgroundColor: '#0A1929' }}>
                <div className="mx-auto w-full max-w-6xl">
                    <GoogleAdBanner
                        slot={process.env.REACT_APP_ADSENSE_HOME_TOP_SLOT}
                        className="mx-auto max-w-6xl"
                        minHeight={140}
                        compact
                    />
                </div>
            </section>

            {/* Sección quienes somos */}
            <section
                className="relative overflow-hidden text-white"
                style={{ backgroundColor: "#2284DF" }}
            >
                <div
                    className="absolute inset-0 pointer-events-none z-0"
                    style={{
                        backgroundColor: "#1F79CF",
                        WebkitMaskImage: "url(/images/backgrounds/textura-azul.png)",
                        maskImage: "url(/images/backgrounds/textura-azul.png)",
                        WebkitMaskRepeat: "repeat",
                        maskRepeat: "repeat",
                        WebkitMaskSize: "1769px 1183px",
                        maskSize: "1769px 1183px",
                        WebkitMaskPosition: "center",
                        maskPosition: "center",
                    }}
                    aria-hidden="true"
                />

                <div className="relative z-10 min-h-[calc(100svh)] flex items-center">
                    <div className="w-full max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8 text-center py-16 md:py-24">
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold tracking-widest uppercase drop-shadow-md">
                            ¿Quiénes somos?
                        </h2>

                        <p className="mt-6 text-sm sm:text-base md:text-lg leading-relaxed tracking-widest uppercase text-white/90">
                            Somos una plataforma de juegos online donde conectamos a jugadores de todo el país.
                        </p>
                        <p className="mt-3 text-sm sm:text-base md:text-lg leading-relaxed tracking-widest uppercase text-white/90">
                            Si te gustan los juegos de mesa argentinos, este es tu lugar. Registrate y conocé más sobre nosotros.
                        </p>
                    </div>
                </div>
            </section>

            {/* Sección juegos disponibles */}
            <section
                className="relative py-16 md:py-24 text-white overflow-hidden"
                style={{
                    backgroundImage: `
          radial-gradient(circle at center, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 30%),
          radial-gradient(circle at center, rgba(0,0,0,0) 55%, rgba(0,0,0,0.25) 100%),
          repeating-conic-gradient(from -90deg at 50% 50%, #2284DF 0deg 12deg, #ffffff 12deg 24deg)
        `,
                    backgroundBlendMode: "normal, normal",
                    backgroundPosition: "center, center",
                    backgroundSize: "cover, cover",
                }}
            >
                <div className="relative max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8">
                    <h2
                        className="toon toon-3d text-2xl sm:text-3xl md:text-6xl font-extrabold uppercase text-center"
                        style={{
                            WebkitTextStroke: "1px #000",
                            textShadow: "0 2px 0 rgba(0,0,0,1), 0 4px 2px rgba(0,0,0,1)",
                        }}
                    >
                        Juegos disponibles
                    </h2>

                    <div className="
            mt-10 grid gap-6 md:gap-8
            [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]
            md:[grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]
            max-w-5xl mx-auto"
                    >
                        {[
                            { title: "TRUCO", img: "", available: true, to: "/games/truco" },
                            { title: "RUMMY", img: "", available: false },
                            { title: "ESCOBA 15", img: "", available: false }
                        ].map((g, i) => (
                            <GameCard key={i} {...g} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Sección Partidas Activas — se colapsa si no hay ninguna */}
            {activeMatches.length > 0 && (
            <section
                className="relative overflow-hidden text-white"
                style={{ backgroundColor: "#2284DF" }}
            >
                <div
                    className="absolute inset-0 pointer-events-none z-0"
                    style={{
                        backgroundColor: "#1F79CF",
                        backgroundImage: "none",
                        WebkitMaskImage: "url(/images/backgrounds/textura-azul.png)",
                        maskImage: "url(/images/backgrounds/textura-azul.png)",
                        WebkitMaskRepeat: "repeat",
                        maskRepeat: "repeat",
                        WebkitMaskSize: "1769px 1183px",
                        maskSize: "1769px 1183px",
                        WebkitMaskPosition: "center",
                        maskPosition: "center",
                    }}
                    aria-hidden="true"
                />

                <div className="relative z-10 max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <h2
                        className="toon text-center text-3xl sm:text-4xl md:text-6xl font-extrabold uppercase text-[#FCE503]"
                        style={{ WebkitTextStroke: "1px #000",
                            textShadow: "0 2px 0 rgba(0,0,0,1), 0 4px 2px rgba(0,0,0,1)" }}
                    >
                        Partidas activas
                    </h2>

                    <p className="mt-6 max-w-2xl mx-auto text-center text-white/90 tracking-wider leading-relaxed">
                        SEGUÍ LAS PARTIDAS EN VIVO DE LA COMUNIDAD EN TIEMPO REAL
                    </p>

                    <div className="mt-10 grid gap-6 md:gap-8 [grid-template-columns:repeat(auto-fill,minmax(300px,360px))] justify-center">
                        {activeMatches.map(m => (
                            <ActiveMatchCard key={m.id} match={m} />
                        ))}
                    </div>

                    <div className="mt-10 flex justify-center">
                        <button
                            onClick={() => navigate('/active-matches')}
                            className="
                              inline-flex items-center justify-center
                              min-w-[260px] md:min-w-[420px]
                              h-7 md:h-10
                              px-6 md:px-8
                              rounded-[14px] md:rounded-[16px]
                              font-bold tracking-wider uppercase
                              text-[#122436]
                              bg-[#FCE503] hover:bg-[#FCE503]/90 transition-colors
                              shadow-[0_10px_30px_rgba(252,229,3,0.35)]
                            "
                        >
                            Ver todas las partidas
                        </button>
                    </div>
                </div>
            </section>
            )}

            <section className="px-4 py-10 sm:px-6 lg:px-8" style={{ backgroundColor: '#122436' }}>
                <div className="mx-auto w-full max-w-4xl">
                    <GoogleAdBanner
                        slot={process.env.REACT_APP_ADSENSE_HOME_MID_SLOT}
                        className="max-w-5xl mx-auto"
                        minHeight={150}
                        compact
                    />
                </div>
            </section>

            {/* Sección Comunidad + Soporte */}
            <section className="relative overflow-hidden text-white -mb-px">
                <div
                    className="absolute inset-0 z-0"
                    style={{
                        backgroundImage:
                            "linear-gradient(to bottom, #122436 0%, #122436 0%, #2284DF 80%, #2284DF 100%)",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />

                <div
                    className="absolute inset-0 z-0 pointer-events-none"
                    style={{
                        backgroundColor: "#2284DF",
                        WebkitMaskImage: "url(/images/backgrounds/textura-azul.png)",
                        maskImage: "url(/images/backgrounds/textura-azul.png)",
                        WebkitMaskRepeat: "repeat",
                        maskRepeat: "repeat",
                        WebkitMaskSize: "1769px 1183px",
                        maskSize: "1769px 1183px",
                        WebkitMaskPosition: "center",
                        maskPosition: "center",
                        transform: "scaleX(-1)",
                    }}
                    aria-hidden="true"
                />

                <div className="relative z-10 max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    {/* Comunidad */}
                    <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 xl:gap-8 overflow-visible">
                            {[
                                {
                                    title: "Comunidad Activa",
                                    text:
                                        "Gran comunidad activa las 24 horas. Conectá con jugadores y equipos de todo el país para apalancarte de sus habilidades.",
                                },
                                {
                                    title: "Torneos Exclusivos",
                                    text:
                                        "Torneos exclusivos organizados con los jugadores de mas alto rango del país con premios increíbles.",
                                },
                                {
                                    title: "Juego Seguro",
                                    text:
                                        "Métodos de pago seguros. Retirá tu recompensa de manera inmediata con Mercado Pago y en un click.",
                                },
                                {
                                    title: "Partidas Rápidas",
                                    text:
                                        "Con el modo partida rápida podés jugar a tus tiempos.",
                                },
                            ].map((item, idx) => (
                                <div key={idx} className="group relative overflow-visible">
                                    <div
                                        aria-hidden="true"
                                        className="
              pointer-events-none absolute
              left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
              w-[160%] h-[220%] md:w-[180%] md:h-[240%]
              opacity-0 transition-opacity duration-200
              group-hover:opacity-100 z-0
            "
                                        style={{
                                            background:
                                                "radial-gradient(ellipse at center," +
                                                " rgba(34,132,223,0.88) 0%," +
                                                " rgba(34,132,223,0.75) 12%," +
                                                " rgba(34,132,223,0.33) 38%," +
                                                " rgba(34,132,223,0.13) 48%," +
                                                " rgba(34,132,223,0.00) 60%)",
                                            filter: "saturate(110%)",
                                        }}
                                    />

                                    <div
                                        className="
              relative z-10 rounded-2xl p-6 text-center
              bg-transparent ring-0
              transition-transform duration-200 will-change-transform
              group-hover:scale-[1.03]
            "
                                    >
                                        <h3
                                            className="
                text-lg md:text-xl font-extrabold uppercase tracking-wider text-white mb-3
                transition-transform duration-200 group-hover:scale-[1.04]
              "
                                        >
                                            {item.title}
                                        </h3>
                                        <p
                                            className="
                text-sm leading-relaxed text-white/85
                transition-transform duration-200 group-hover:scale-[1.02]
              "
                                        >
                                            {item.text}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex justify-center">
                            {isAuthenticated ? (
                                <GamingButton
                                    to="/games"
                                    variant="ctaYellow"
                                    className="min-w-[260px] md:min-w-[420px]"
                                >
                                    JUGAR AHORA
                                </GamingButton>
                            ) : (
                                <GamingButton
                                    to="/register"
                                    variant="ctaYellow"
                                    className="min-w-[260px] md:min-w-[420px]"
                                >
                                    REGISTRATE GRATIS
                                </GamingButton>
                            )}
                        </div>
                    </div>

                    {/* Soporte */}
                    <div className="mt-16 md:mt-64">
                        <h3 className="text-xl md:text-2xl font-extrabold tracking-widest uppercase mb-3">» Soporte</h3>
                        <p className="max-w-3xl text-white/90 leading-relaxed mb-8">
                            Gestionamos tus pagos con seguridad y transparencia. Nuestro equipo está disponible 24/7
                            para asistirte, y los retiros de ganancias son simples y rápidos.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                            {[
                                {
                                    title: "Pagos seguros",
                                    text: "Todas las transacciones están protegidas con encriptación de nivel bancario.",
                                    icon: "/images/icons/escudo.png",
                                },
                                {
                                    title: "Soporte 24/7",
                                    text: "Nuestro equipo está disponible las 24 horas para asistirte.",
                                    icon: "/images/icons/auri.png",
                                },
                                {
                                    title: "Retiros rápidos",
                                    text: "Procesamos tus ganancias en menos de 24 horas hábiles.",
                                    icon: "/images/icons/billete.png",
                                },
                                {
                                    title: "Historial completo",
                                    text: "Acceso total a tu historial de transacciones y ganancias.",
                                    icon: "/images/icons/reloj.png",
                                },
                            ].map((s, i) => (
                                <div
                                    key={i}
                                    className="rounded-2xl bg-[#122436] ring ring-white/90 p-5 md:p-6
                       shadow-[0_12px_30px_rgba(0,0,0,0.35)] min-h-[180px] flex flex-col"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-white font-extrabold uppercase tracking-wider">{s.title}</h4>
                                        <img src={s.icon} alt="" className="w-6 h-6 object-contain" />
                                    </div>
                                    <p className="text-sm text-white/85 leading-relaxed">{s.text}</p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex justify-center">
                            <button
                                className="
            inline-flex items-center justify-center
            min-w-[260px] md:min-w-[420px]
            h-12 md:h-14
            px-6 md:px-8
            rounded-[14px] md:rounded-[16px]
            font-bold tracking-wider uppercase
            text-[#122436]
            bg-[#FCE503] hover:bg-[#FCE503]/90 transition-colors
            shadow-[0_10px_30px_rgba(252,229,3,0.35)]
          "
                            >
                                Contactar
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Preguntas Frecuentes */}
            <section className="relative py-16 md:py-24 -mt-px" style={{ backgroundColor: SECTION_BG }}>
                <div className="mx-auto max-w-[calc(100%-100px)] px-4 sm:px-6 lg:px-8">
                    <div className="mb-10 md:mb-12">
                        <h3 className="text-white/90 font-extrabold tracking-widest uppercase text-lg md:text-xl">
                            » Preguntas frecuentes
                        </h3>
                        <p className="mt-4 max-w-xl text-white/85 leading-relaxed">
                            Encontrá respuestas a las dudas más comunes sobre la plataforma,
                            procesos de juego y pagos.
                        </p>
                    </div>

                    <div
                        className="mx-auto w-full max-w-5xl rounded-[28px] overflow-hidden"
                        style={{
                            border: `2px solid ${borderCol}`,
                            boxShadow: "0 0 0 1px rgba(255,255,255,0.06) inset",
                        }}
                    >
                        {faqs.map((f, i) => {
                            const isOpen = open === i;
                            return (
                                <div
                                    key={i}
                                    className="relative"
                                    style={{ borderTop: i === 0 ? "none" : `1px solid ${borderCol}` }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setOpen(isOpen ? null : i)}
                                        className="
                    w-full select-none
                    flex items-center justify-between gap-4
                    px-6 md:px-8 py-5 md:py-6
                    text-left text-white/90
                    transition-colors hover:bg-white/5
                  "
                                        style={{ backgroundColor: panelBg }}
                                    >
                  <span
                      className={`
                      transition-all tracking-wide uppercase
                      ${isOpen ? "font-extrabold text-base md:text-lg" : "font-semibold text-sm md:text-base"}
                    `}
                  >
                    {f.q}
                  </span>
                                        <svg
                                            className={`shrink-0 h-5 w-5 transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            aria-hidden="true"
                                        >
                                            <path d="M6 9l6 6 6-6" />
                                        </svg>
                                    </button>

                                    <div
                                        className={`
                    px-6 md:px-8
                    overflow-hidden
                    transition-all duration-300 ease-out
                    ${isOpen ? "max-h-[320px] opacity-100 py-4 md:py-5" : "max-h-0 opacity-0 py-0"}
                  `}
                                        style={{ backgroundColor: panelBg }}
                                    >
                                        <p className="text-white/85 leading-relaxed">{f.a}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default NewUIHome;